import SearchIcon from '@mui/icons-material/Search';
import { Box, List, ListItem, ListItemButton, ListItemText, Paper } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useApiClient } from '../hooks/useApiClient';
import { ChatMessageTemplateService } from '../services';
import { LiveChatData } from '../types/client';
import { Search, SearchIconWrapper, StyledInputBase } from './common/StyledSearchComponents';

interface AgentSearchResult {
  content: string;
  category: string;
}

interface ChatAgentSearchProps {
  isInputDisabled: boolean;
  selectedLanguage: string;
  onMessageSelect: (content: string) => void;
  chatData?: LiveChatData | null;
}

const ChatAgentSearch: React.FC<ChatAgentSearchProps> = ({
  isInputDisabled,
  selectedLanguage,
  onMessageSelect,
  chatData
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<AgentSearchResult[]>([]);
  const apiClient = useApiClient(chatData);
  // Use only chatData.token
  const token = chatData?.token || null;
  const messageServiceRef = useRef<ChatMessageTemplateService | null>(null);

  // Initialize message service for search
  useEffect(() => {
    messageServiceRef.current = new ChatMessageTemplateService(apiClient);
  }, [apiClient]);

  const runSearch = async () => {
    if (!messageServiceRef.current || !searchInput.trim()) {
      setSearchResults([]);
      return;
    }

    // Check if token exists before making API call
    if (!token) {
      console.warn('Cannot search: Authentication token is missing');
      setSearchResults([]);
      return;
    }

    try {
      const results = await messageServiceRef.current.searchAgent({
        search: searchInput.trim(),
        lang_code: selectedLanguage
      });
      setSearchResults(results);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    }
  };

  const handleResultClick = (content: string) => {
    onMessageSelect(content);
    setSearchResults([]);
    setSearchInput('');
  };

  return (
    <Box flex={1} sx={{ position: 'relative' }}>
      {searchResults.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            maxHeight: '300px',
            overflow: 'auto',
            mb: 1,
            zIndex: 1000
          }}
        >
          <List dense>
            {searchResults.map((result, index) => (
              <ListItem key={index} disablePadding>
                <ListItemButton
                  onClick={() => handleResultClick(result.content)}
                >
                  <ListItemText primary={result.content} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Search sx={{ flex: 1 }} className={isInputDisabled ? 'chatyme-disabled-search' : ''}>
        <SearchIconWrapper>
          <SearchIcon color="action" />
        </SearchIconWrapper>
        <StyledInputBase
          disabled={isInputDisabled}
          name="searchInput"
          placeholder="Search…"
          inputProps={{ 'aria-label': 'search' }}
          value={searchInput}
          onChange={e => {
            setSearchInput(e.target.value);
            if (!e.target.value.trim()) {
              setSearchResults([]);
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              runSearch();
            }
          }}
        />
      </Search>
    </Box>
  );
};

export default ChatAgentSearch;
